import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Button, Callout, FormGroup, Icon, InputGroup, Intent
} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';

import SelectOptions from '../components/SelectOptionsFormElement';
import {
  getColumnsFromSource,
  sourceDataToSparklineData,
  getColorByName,
  getColorByIndex,
} from './utils';
import SparklineChart from './SparklineChart';
import {COMPARE_TO, COMPARE_TO_OPTIONS} from './constants';
import CollapseCard from './CollapseCard';
import KPIChartHeader from './KPIChartHeader';
import NumberFormat from './NumberFormat';

import './KPICardForm.scss';

interface KPICardFormProps {
  uid: string;
  disabled?: boolean;
  column: string;
  label: string;
  sourceUid?: string;
  sourceData: any;
  format: string;
  isPercentage: boolean;
  comparison?: {
    show: boolean;
    type: string;
    column?: string;
    format?: string;
    is_percentage?: boolean;
  }
  sparkline?: {
    show: boolean;
    column?: string;
    color?: string;
  };
  onColumnChange: (value: string) => void;
  onLabelChange: (value: string) => void;
  onFormatIsPercentageChange: (format: string, isPercentage: boolean) => void;
  onComparisonChange: (value: any) => void;
  onSparklineChange?: (key: string) => (value: any) => void;
}

const NoSourceData = () => {
  return (
    <Callout
      title="No source data"
      intent={Intent.WARNING}
    >
      <p>
        The source content has not been executed or has no data. Please execute the source content first.
      </p>
    </Callout>
  );
};

export const KPICardForm = ({
  uid,
  disabled,
  column,
  label,
  format,
  isPercentage,
  sourceUid,
  sourceData,
  comparison,
  sparkline,
  onColumnChange,
  onLabelChange,
  onFormatIsPercentageChange,
  onComparisonChange,
  onSparklineChange,
}: KPICardFormProps) => {
  const sourceColumns = useMemo(() => getColumnsFromSource(sourceData), [sourceData]);
  /*
   * Other parts of Form are dependent on these states, that's why we save them locally.
   */
  const [color, setColor] = useState(getColorByName(sparkline?.color).color);
  const [sparklineShow, setSparklineShow] = useState(sparkline?.show || false);
  const [sparklineColumn, setSparklineColumn] = useState(sparkline?.column || '');
  const [compareToShow, setCompareToShow] = useState(comparison?.show || false);
  const [compareToType, setCompareToType] = useState(comparison?.type || COMPARE_TO.PREVIOUS_ROW);
  const [compareToColumn, setCompareToColumn] = useState(comparison?.column as string);
  const [compareToFormat, setCompareToFormat] = useState(comparison?.format || '');
  const [
    compareToIsPercentage,
    setCompareToIsPercentage
  ] = useState(comparison?.is_percentage || false);

  const _onColumnChange = (value: any) => {
    onColumnChange(value.value);
  }

  const _onLabelChange = (event: any) => {
    onLabelChange(event.target.value);
  }

  const onColorChange = () => {
    const currentColor = getColorByName(color.name);
    const nextColor = getColorByIndex(currentColor.index + 1);
    setColor(nextColor.color);
    onSparklineChange && onSparklineChange('color')(nextColor.color.name);
  };

  const onSparklineColumnChange = (value: any) => {
    setSparklineColumn(value.value);
    onSparklineChange && onSparklineChange('column')(value.value);
  };

  const onShowSparklineChange = (value: boolean) => {
    setSparklineShow(value);
    onSparklineChange && onSparklineChange('show')(value);
  };

  const _onComparisonChange = (key: string, value: any) => {
    onComparisonChange({
      ...comparison,
      show: compareToShow,
      column: compareToColumn,
      type: compareToType,
      format: compareToFormat,
      is_percentage: compareToIsPercentage,
      [key]: value,
    });
  }

  const onShowComparisonChange = (value: boolean) => {
    setCompareToShow(value);
    _onComparisonChange('show', value);
  };

  const onCompareToTypeChange = (value: any) => {
    setCompareToType(value.value);
    _onComparisonChange('type', value.value);
  };

  const onCompareToColumnChange = (value: any) => {
    setCompareToColumn(value.value);
    _onComparisonChange('column', value.value);
  }

  const onCompareToFormatChange = (value: string) => {
    setCompareToFormat(value);
    _onComparisonChange('format', value);
  }

  const onCompareToIsPercentageChange = (value: boolean) => {
    setCompareToIsPercentage(value);
    _onComparisonChange('is_percentage', value);
  }

  const onCompareToFormatIsPercentageChange = (format: string, isPercentage: boolean) => {
    /*
     * TODO: review this
     */
    setCompareToFormat(format);
    setCompareToIsPercentage(isPercentage);

    onComparisonChange({
      ...comparison,
      show: compareToShow,
      column: compareToColumn,
      type: compareToType,
      format,
      is_percentage: isPercentage,
    });
  }

  if (sourceUid && !sourceData && !sourceData?.value) {
    return <NoSourceData />;
  }

  return (
    <div className="kpi-card-form">
      <div className="kpi-card-form__content">
        <CollapseCard title="Basic Settings" open={true}>
          <SelectOptions
            selected={column}
            onChange={_onColumnChange}
            label="Column"
            options={sourceColumns}
            selectLabel="Select Column"
            disabled={disabled}
          />
          <FormGroup inline={true} label="Label">
            <InputGroup
              defaultValue={label || ''}
              className="kpi-card-form__input"
              onChange={_onLabelChange}
              autoComplete="off"
              placeholder="Column Label"
              disabled={disabled}
            />
          </FormGroup>
          <NumberFormat
            isPercentage={isPercentage}
            value={format}
            onChange={onFormatIsPercentageChange}
          />
        </CollapseCard>
        <CollapseCard
          title="Comparison"
          showSwitch={true}
          open={compareToShow}
          switchValue={compareToShow}
          onSwitchChange={onShowComparisonChange}
        >
          <SelectOptions
            selected={compareToType}
            onChange={onCompareToTypeChange}
            label="Compare to"
            options={COMPARE_TO_OPTIONS}
            disabled={disabled}
          />
          {
            (compareToType === COMPARE_TO.ANOTHER_COLUMN) &&
            <SelectOptions
              selected={compareToColumn}
              onChange={onCompareToColumnChange}
              label="Column"
              options={sourceColumns}
              selectLabel="Select Column"
              disabled={disabled}
            />
          }
          <NumberFormat
            isPercentage={compareToIsPercentage}
            value={compareToFormat}
            onChange={onCompareToFormatIsPercentageChange}
            disabled={disabled}
          />
        </CollapseCard>
        <CollapseCard
          title="Sparkline"
          showSwitch={true}
          open={sparklineShow}
          switchValue={sparklineShow}
          onSwitchChange={onShowSparklineChange}
        >
          <div className="kpi-card-form__content__sparkline">
            <SelectOptions
              selected={sparklineColumn}
              onChange={onSparklineColumnChange}
              label="X-Axis"
              options={sourceColumns}
              selectLabel="Select X-Axis Column"
              disabled={disabled}
            />
            <Button
              className="sparkline-color"
              small={true}
              minimal={true}
              outlined={true}
              icon={<Icon icon={IconNames.TINT} color={color.primary} size={16}/>}
              onClick={onColorChange}
              disabled={disabled}
            />
          </div>
        </CollapseCard>
      </div>
      <div className="kpi-card-form__chart">
        <KPIChartHeader
          label={label}
          column={column}
          format={format}
          isPercentage={isPercentage}
          sourceData={sourceData}
          compareToShow={compareToShow}
          compareToType={compareToType}
          compareToColumn={compareToColumn}
          compareToFormat={compareToFormat}
          compareToIsPercentage={compareToIsPercentage}
        />
        {
        sparklineShow &&
          <SparklineChart
            uid={uid as string}
            data={sourceDataToSparklineData(sourceData, column, sparklineColumn)}
            color={color}
          />
        }
      </div>
    </div>
  )
};

export default KPICardForm;
